"use client"

import { useEffect, useState } from "react"
import Link from "next/link"
import { ArrowRightIcon, Loader2 } from "lucide-react"

import { mapStripeDataToProduct } from "@/lib/utils/product-mapper"
import { Button } from "@/components/ui/button"
import ProductDetails from "@/components/supervvisor/supervvisor-product-details"
import { ProductData } from "@/app/types/product"

interface ProductPageProps {
  params: {
    productId: string
  }
  searchParams: {
    returnUrl?: string
  }
}
export default function ProductPage({
  params,
  searchParams,
}: ProductPageProps) {
  const { productId } = params
  const { returnUrl } = searchParams
  const [productData, setProductData] = useState<ProductData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    const fetchProductData = async () => {
      try {
        const response = await fetch(`/api/stripe/products?id=${productId}`)
        if (!response.ok) {
          throw new Error("Failed to fetch product data")
        }
        const data = await response.json()
        const mappedProduct = mapStripeDataToProduct(data)
        setProductData(mappedProduct[0])
      } catch (err) {
        setError("Error fetching product data")
      } finally {
        setLoading(false)
      }
    }
    fetchProductData()
  }, [productId])

  if (loading) {
    return (
      <div className="flex items-center justify-center text-primary">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading product details...</span>
      </div>
    )
  }

  if (!productData) {
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center space-y-4">
        <div className="text-2xl font-semibold">Product Not Found</div>
        <p className="text-muted-foreground">
          The product you&apos;re looking for doesn&apos;t exist or has been
          removed.
        </p>
        <div>
          <Button asChild>
            <Link href="/products">
              Continue to Products <ArrowRightIcon className="ml-1.5 size-4" />
            </Link>
          </Button>
        </div>
      </div>
    )
  }
  return (
    <div>
      <ProductDetails
        product={productData}
        returnUrl={returnUrl || "/products"}
      />
    </div>
  )
}
